import { Base } from "ui/Scripts/source/framework/base";
import { IUnitDefaultHandlersNodeUser, UnitDefaultHandlersNodeUser } from "./unitDefaultHandlersNodeUser";
import { UnitDefaultHandlersNodeSave } from "./unitDefaultHandlersNodeSave";
import { Translations } from "ui/Scripts/source/models/translations";
import { DefaultHandlerType } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/enums";

export interface IUnitDefaultHandlersNode {
    id: string;
    name: string;
    explicitUsers: IUnitDefaultHandlersNodeUser[];
    children: IUnitDefaultHandlersNode[];
    parent: IUnitDefaultHandlersNode;
    partnerCompanyIds: string[];

    getIsRoot(): boolean;
    getSiblings(): IUnitDefaultHandlersNode[];
    hasChildren(): boolean;
    hasSiblings(): boolean;
    hasExplicitUsers(): boolean;
    getDefaultUsersText(type?: DefaultHandlerType): string;
    getUnitNodeById(id: string): IUnitDefaultHandlersNode;
    setUser(userCode: string, type: number, name: string, picture: string, clearDescendants: boolean);
    getUsers(): IUnitDefaultHandlersNodeUser[];
    getUnitDefaultHandlersNodeSaves(list: UnitDefaultHandlersNodeSave[]): UnitDefaultHandlersNodeSave[];
    removeUsers(processSubUnits: boolean);
}

export class UnitDefaultHandlersNode implements IUnitDefaultHandlersNode {
    id: string;
    name: string;
    explicitUsers: UnitDefaultHandlersNodeUser[];
    children: UnitDefaultHandlersNode[];
    parent: UnitDefaultHandlersNode;
    partnerCompanyIds: string[];

    constructor();
    constructor(obj: IUnitDefaultHandlersNode);
    constructor(obj?: any) {
        this.id = obj && obj.id || "";
        this.name = obj && obj.name || "";
        this.explicitUsers = null;
        if (obj && obj.explicitUsers) {
            this.explicitUsers = Base.getTypedArray(obj ? obj.explicitUsers : null, UnitDefaultHandlersNodeUser);
        }
        this.children = [];
        this.parent = null;
        if (obj && obj.children) {
            this.children = Base.getTypedArray(obj.children, UnitDefaultHandlersNode);
            for (const child of this.children) {
                child.parent = this;
            }
        }
        this.partnerCompanyIds = obj && obj.partnerCompanyIds || [];
    }

    getIsRoot(): boolean {
        return !this.parent;
    }

    getRoot(): UnitDefaultHandlersNode {
        return this.getIsRoot() ? this : this.parent.getRoot();
    }

    getSiblings(): UnitDefaultHandlersNode[] {
        if (this.getIsRoot()) return [];
        return this.parent.children.filter(i => i.id !== this.id);
    }

    getPathNodes(): UnitDefaultHandlersNode[] {
        if (this.getIsRoot()) return [this];
        const result: UnitDefaultHandlersNode[] = this.parent.getPathNodes();
        result.push(this);
        return result;
    }

    hasChildren(): boolean {
        return this.children.length > 0;
    }

    hasSiblings(): boolean {
        if (this.getIsRoot()) return false;
        return this.parent.children.length > 1;
    }

    hasExplicitUsers(): boolean {
        return !!this.explicitUsers && this.explicitUsers.length > 0;
    }

    getUnitNodeById(id: string): IUnitDefaultHandlersNode {
        if (!id) return null;
        if (this.id.equalIgnoreCase(id)) return this;
        for (const child of this.children) {
            const childResult = child.getUnitNodeById(id);
            if (childResult) return childResult;
        }
        return null;
    }

    removeUser(userCode: string) {
        if (this.explicitUsers === null || this.explicitUsers.length === 0) return;
        const index = this.explicitUsers.findIndex(i => i.code.equalIgnoreCase(userCode));
        if (index >= 0) {
            this.explicitUsers.splice(index, 1);
        }
    }

    removeUsers(processSubUnits: boolean = false) {
        if (this.explicitUsers !== null && this.explicitUsers.length > 0) {
            this.explicitUsers = null;
        }
        if (!processSubUnits || !this.hasChildren()) return;
        for (let i = 0; i < this.children.length; i++) {
            this.children[i].removeUsers(true);
        }
    }

    clearSubUnits() {
        if (!this.hasChildren()) return;
        for (const child of this.children) {
            child.removeUsers(true);
        }
    }

    setUser(userCode: string, type: number, name: string, picture: string, clearDescendants: boolean) {
        let currentUser: UnitDefaultHandlersNodeUser = null;
        if (this.explicitUsers === null) {
            this.explicitUsers = [];
        } else {
            currentUser = this.explicitUsers.find(j => j.code.equalIgnoreCase(userCode));
        }
        if (!currentUser) {
            currentUser = new UnitDefaultHandlersNodeUser();
            currentUser.code = userCode;
            currentUser.type = type;
            currentUser.name = name;
            currentUser.picture = picture;
            this.explicitUsers.push(currentUser);
        } else {
            currentUser.type = type;
        }
        //Clear subfolder permissions
        if (clearDescendants) {
            this.clearSubUnits();
        }
        if (this.explicitUsers.length < 2) return;
        this.explicitUsers = UnitDefaultHandlersNodeUser.sortUnitDefaultHandlersNodeUsers(this.explicitUsers, true);
    }

    getUsers(): UnitDefaultHandlersNodeUser[] {
        if (this.getIsRoot() || this.explicitUsers != null) {
            return this.explicitUsers;
        } else {
            return this.parent.getUsers();
        }
    }

    getSubTreeUserCodes(list: string[]): string[] {
        if (this.explicitUsers != null && this.explicitUsers.length > 0) {
            for (let i = 0; i < this.explicitUsers.length; i++) {
                const code = this.explicitUsers[i].code;
                if (list.indexOf(code) > -1) continue;
                list.push(code);
            }
        }
        if (this.children.length < 1) return list;
        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            child.getSubTreeUserCodes(list);
        }
        return list;
    }

    getAsList(list: UnitDefaultHandlersNode[]): UnitDefaultHandlersNode[] {
        list.push(this);
        if (this.children.length < 1) return list;
        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            child.getAsList(list);
        }
        return list;
    }

    getUnitDefaultHandlersNodeSaves(list: UnitDefaultHandlersNodeSave[]): UnitDefaultHandlersNodeSave[] {
        let i: number;
        let users: UnitDefaultHandlersNodeUser[] = [];
        if (this.explicitUsers !== null && this.explicitUsers.length > 0) {
            users = this.explicitUsers.slice(0);
        }
        let saveUnitNode = list.find(j => j.id === this.id);
        if (!saveUnitNode) {
            saveUnitNode = new UnitDefaultHandlersNodeSave();
            saveUnitNode.id = this.id;
            list.push(saveUnitNode);
        }
        for (i = 0; i < users.length; i++) {
            const user = users[i];
            if (saveUnitNode.userCodes.findIndex(j => j.equalIgnoreCase(user.code)) >= 0) continue;
            saveUnitNode.userCodes.push(user.code);
            if (user.type === DefaultHandlerType.Handler || saveUnitNode.notifyUserCodes.findIndex(j => j.equalIgnoreCase(user.code)) >= 0) continue;
            saveUnitNode.notifyUserCodes.push(user.code);
        }
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getUnitDefaultHandlersNodeSaves(list);
        }
        return list;
    }

    getTreeHasPartnerCompanies(): boolean {
        if (this.partnerCompanyIds.length > 0) return true;
        for (const child of this.children) {
            const childResult = child.getTreeHasPartnerCompanies();
            if (childResult) return true;
        }
        return false;
    }
    
    addPartnerCompanyToUnit(unitId: string, partnerCompanyId: string) {
        if (!unitId || !partnerCompanyId) return;
        const node = this.getUnitNodeById(unitId);
        if (!node) return;
        if (node.partnerCompanyIds.indexOf(partnerCompanyId) > -1) return;
        node.partnerCompanyIds.push(partnerCompanyId);
    }

    removePartnerCompanyFromTree(unitId: string, partnerCompanyId: string) {
        if (!unitId || !partnerCompanyId) return;
        const node = this.getUnitNodeById(unitId);
        if (!node) return;
        const index = node.partnerCompanyIds.indexOf(partnerCompanyId);
        if (index < 0) return;
        node.partnerCompanyIds.splice(index, 1);
    }

    getPartnerCompanyIdsFromTree(unitId: string): string[] {
        if (!unitId) return [];
        const node = this.getUnitNodeById(unitId);
        if (!node) return [];
        return node.partnerCompanyIds.slice(0);
    }

    getDefaultUsersText(type?: DefaultHandlerType): string {
        let users = this.getUsers() ?? [];
        if (!Base.isNullOrUndefined(type)) {
            users = users.filter(i => i.type === type);
        }
        const result = Base.getStringWithSeparators(users.map(i => i.name), ", ");
        return result?.length ? result : "'" + Translations.NotSet + "'";
    }
}